/**
 * Hash-route patterns (GDD §4.5 notation) → matchers and builders. Pure (no DOM), unit-tested; F1's router builds on it.
 *
 * Pattern syntax: `#/arena/fight/:fightId`, optional groups in brackets `#/life[/:tab[/:sub]]`, the charter
 * `#duel=:code`. A hash may carry a query after `?` (`#/cards/match?npc=…&deck=…`), merged into the params.
 * Precedence: among matching patterns the one with the FEWEST bound parameters wins, so literal routes
 * (`#/life/day`, `#/life/gear/workshop`) beat the parametric `#/life[/:tab[/:sub]]`.
 */
import type { ScreenDef, ScreenParams } from '../shared';

export interface CompiledRoute { pattern: string; regex: RegExp; names: string[] }

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|\\/]/g, '\\$&');

/** Compiles one GDD §4.5 route pattern. */
export function compileRoute(pattern: string): CompiledRoute {
  const names: string[] = [];
  let re = '';
  for (let i = 0; i < pattern.length; ) {
    const c = pattern[i];
    if (c === '[') { re += '(?:'; i++; continue; }
    if (c === ']') { re += ')?'; i++; continue; }
    if (c === ':') {
      const m = /^:([A-Za-z_][A-Za-z0-9_]*)/.exec(pattern.slice(i));
      if (!m) throw new Error(`route ${pattern}: bad parameter at ${i}`);
      names.push(m[1]);
      re += '([^/?#]+)';
      i += m[0].length;
      continue;
    }
    re += escapeRe(c);
    i++;
  }
  return { pattern, regex: new RegExp(`^${re}/?$`), names };
}

/** Matches a hash (with optional `?query`) against a compiled route; returns params or null. */
export function matchRoute(route: CompiledRoute, hash: string): { params: ScreenParams; bound: number } | null {
  const q = hash.indexOf('?');
  const path = q >= 0 ? hash.slice(0, q) : hash;
  const m = route.regex.exec(path);
  if (!m) return null;
  const params: ScreenParams = {};
  let bound = 0;
  route.names.forEach((n, i) => {
    const v = m[i + 1];
    if (v !== undefined) { params[n] = decodeURIComponent(v); bound++; }
  });
  if (q >= 0) new URLSearchParams(hash.slice(q + 1)).forEach((v, k) => { params[k] = v; });
  return { params, bound };
}

/** Builds a hash from a pattern and params; optional groups whose parameters are missing are dropped. Extra params
 *  (not in the pattern) are appended as a `?query`. Throws when a required parameter is missing. */
export function buildHash(pattern: string, params: ScreenParams = {}): string {
  const used = new Set<string>();
  const fill = (s: string): { text: string; complete: boolean } => {
    let out = '';
    let complete = true;
    for (let i = 0; i < s.length; ) {
      if (s[i] === '[') {
        let depth = 1, j = i + 1;
        while (j < s.length && depth > 0) { if (s[j] === '[') depth++; else if (s[j] === ']') depth--; j++; }
        const inner = fill(s.slice(i + 1, j - 1));
        if (inner.complete) out += inner.text;
        i = j;
        continue;
      }
      if (s[i] === ':') {
        const m = /^:([A-Za-z_][A-Za-z0-9_]*)/.exec(s.slice(i))!;
        const v = params[m[1]];
        if (v === undefined || v === '') complete = false;
        else { out += encodeURIComponent(String(v)); used.add(m[1]); }
        i += m[0].length;
        continue;
      }
      out += s[i];
      i++;
    }
    return { text: out, complete };
  };
  const { text, complete } = fill(pattern);
  if (!complete) throw new Error(`route ${pattern}: missing a required parameter`);
  const extra = Object.entries(params).filter(([k, v]) => !used.has(k) && v !== undefined);
  if (extra.length === 0) return text;
  const qs = new URLSearchParams(extra.map(([k, v]) => [k, String(v)])).toString();
  return `${text}?${qs}`;
}

/** Resolves a hash to the best-matching screen (fewest bound parameters wins). */
export function resolveHash(defs: readonly ScreenDef[], hash: string): { def: ScreenDef; params: ScreenParams } | null {
  let best: { def: ScreenDef; params: ScreenParams; bound: number } | null = null;
  for (const def of defs) {
    if (!def.route) continue;
    const m = matchRoute(compileRoute(def.route), hash);
    if (m && (!best || m.bound < best.bound)) best = { def, params: m.params, bound: m.bound };
  }
  return best && { def: best.def, params: best.params };
}

/** Finds a screen by id or alias (the `?screen=` / `view` debug entry). */
export function findScreen(defs: readonly ScreenDef[], idOrAlias: string): ScreenDef | null {
  return defs.find((d) => d.id === idOrAlias) ?? defs.find((d) => d.aliases?.includes(idOrAlias)) ?? null;
}

/** Hash redirects (GDD G-07: `#/life/deeds` → `#/life/honour`; «Почёт» was called «Деяния»). Prefix match. */
export const ROUTE_REDIRECTS: ReadonlyArray<readonly [from: string, to: string]> = [
  ['#/life/deeds', '#/life/honour'],
];

/** Applies {@link ROUTE_REDIRECTS} (keeps any sub-path and query); returns the input when nothing matches. */
export function redirectHash(hash: string): string {
  for (const [from, to] of ROUTE_REDIRECTS) {
    if (hash === from || hash.startsWith(`${from}/`) || hash.startsWith(`${from}?`)) return to + hash.slice(from.length);
  }
  return hash;
}

/** Path depth of a hash route: `#/arena` 1, `#/arena/fight/f1` 3, `#duel=…` 1 (query ignored). */
export function hashDepth(hash: string): number {
  const path = hash.split('?')[0].replace(/^#\/?/, '');
  return path === '' ? 0 : path.split('/').filter(Boolean).length;
}

/**
 * The nearest ancestor hash that resolves to a non-sheet screen (a routed sheet's base on a deep link, and the target
 * of «back» when the app was opened on a sub-page): `#/life/gear/workshop` → `#/life/gear`, `#/arena/result/f1` →
 * `#/arena`. Returns null when no ancestor resolves.
 */
export function parentHash(defs: readonly ScreenDef[], hash: string): string | null {
  const path = hash.split('?')[0];
  if (!path.startsWith('#/')) return null;
  const parts = path.slice(2).split('/').filter(Boolean);
  for (let n = parts.length - 1; n >= 1; n--) {
    const h = `#/${parts.slice(0, n).join('/')}`;
    const hit = resolveHash(defs, h);
    if (hit && hit.def.kind !== 'sheet' && hit.def.kind !== 'sheetPage') return h;
  }
  return null;
}
